import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowDown } from "lucide-react";
import { sutras } from "../data/sutras";
import { useCopy } from "@/lib/copyVariant";

const LOGO_SVG = "https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/ne3mafa3_65cc7dd2927713da7465df9f_calma.svg";
const LOGO_PNG = "https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/hxuq3qau_Untitled%20design%20%289%29.png";
const CDN = "https://cdn.prod.website-files.com/5e95b7d352d2e8c65a55a3b9/";
const MONKEY_BACK = "https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/wowr1xlo_Individual%20cards%201-1.png";

const navLinks = [
  { label: "Story", href: "#showcase" },
  { label: "Cards", href: "#gallery" },
  { label: "Sutras", href: "#sutras" },
  { label: "Author", href: "#author" },
  { label: "Gift", href: "#gift" },
  { label: "Connect", href: "#contact" },
];

// Card image pool - randomly drawn for hero scatter on each mount
const CARD_POOL = [
  `${CDN}65cc93c32afa8fe13eac92fd_A%20diamond.jpg`,
  `${CDN}65cc93c346f50d72ae8e0c2b_2%20diamond.jpg`,
  `${CDN}65cc93c3927713da7471214e_3%20diamond.jpg`,
  `${CDN}65cc93c52512d0a2d979e2c8_4%20diamond.jpg`,
  `${CDN}65cc93c3b7d6f1cef3c7556e_5%20diamond.jpg`,
  `${CDN}65cc93c342d58de33d6adb51_7%20diamond.jpg`,
  `${CDN}65cc93c3b8c0d5d11e870e05_8%20diamond.jpg`,
  `${CDN}65cc93c3ff9ceca1fb59dca1_10%20diamond.jpg`,
  `${CDN}65cd02ffceed6ae8e6262ddd_A%20heart.jpg`,
  `${CDN}65cd0300a8a10d3606fc0d50_3%20heart.jpg`,
  `${CDN}65cd0300febad35d7c575b46_5%20heart.jpg`,
  `${CDN}65cd03001fed3a3cba44c8c9_7%20heart.jpg`,
  `${CDN}65cd0300a42f14b51c54ff3a_8%20heart.jpg`,
  `${CDN}65cd030078e5e53f9d0a11dc_9%20heart.jpg`,
  `${CDN}65cd03b78284f32e346a6e79_A%20spade.jpg`,
  `${CDN}65cd03b89bfea9513f3bcaa6_3%20spade.jpg`,
  `${CDN}65cd03b70bb1abaa472b1c74_5%20spade.jpg`,
  `${CDN}65cd03b7edad5c979bda8a7e_7%20spade.jpg`,
  `${CDN}65cd03b7c0730963cdfa1646_9%20spade.jpg`,
  `${CDN}65cd04856286d65c8f9ba1b8_A%20clubs.jpg`,
  `${CDN}65cd0485e9a20fc358bb9841_3%20clubs.jpg`,
  `${CDN}65cd04858c7b74f8f6aa5de9_5%20clubs.jpg`,
  `${CDN}65cd048547449e818eef1a19_7%20clubs.jpg`,
  `${CDN}65cd0485caeb8b580fd0dfd7_9%20clubs.jpg`,
  `${CDN}65cd05476dfde65f950a38eb_J%20diamond.jpg`,
  `${CDN}65cd054758ac712906a05f74_J%20heart.jpg`,
  `${CDN}65cd05474ea4afac7e722825_J%20spade.jpg`,
  `${CDN}65cd05476f3823b53ca58686_Q%20diamond.jpg`,
  `${CDN}65cd054887a0369e9dc5dca5_Q%20heart.jpg`,
  `${CDN}65cd0547f98efff420f7693c_K%20diamond.jpg`,
  `${CDN}65cd0547b4283abd8ea6276c_K%20heart.jpg`,
  `${CDN}65cd0547b4283abd8ea6278b_K%20spade.jpg`,
  `${CDN}65cd0547a7425e9854fd9ac9_Joker%206.jpg`,
  MONKEY_BACK,
];

const rand = (min, max) => min + Math.random() * (max - min);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Slot zones - each card lands inside one zone. The 4 corner zones are mobile-visible (always),
// and outer/inner extras progressively appear on larger breakpoints.
const SLOT_ZONES = [
  // 4 corners - always visible (mobile shows just these, framing the centered content)
  { x: [3, 7],  y: [3, 7],   side: "tl", rot: [-18, -8],  vis: "" },
  { x: [3, 7],  y: [3, 7],   side: "tr", rot: [8, 18],    vis: "" },
  { x: [3, 7],  y: [5, 10],  side: "bl", rot: [-16, -6],  vis: "" },
  { x: [3, 7],  y: [5, 10],  side: "br", rot: [6, 16],    vis: "" },
  // sm+ - mid edges
  { x: [1, 4],  y: [38, 46], side: "ml", rot: [-12, -4],  vis: "hidden sm:block" },
  { x: [1, 4],  y: [40, 48], side: "mr", rot: [4, 12],    vis: "hidden sm:block" },
  // md+ - slightly inward upper
  { x: [14, 19],y: [16, 24], side: "tl", rot: [-9, -2],   vis: "hidden md:block" },
  { x: [12, 17],y: [18, 26], side: "tr", rot: [2, 9],     vis: "hidden md:block" },
  // lg+ - inner mid
  { x: [22, 28],y: [50, 58], side: "bl", rot: [-7, 0],    vis: "hidden lg:block" },
  { x: [18, 24],y: [48, 56], side: "br", rot: [3, 10],    vis: "hidden lg:block" },
  // xl+ - far inner
  { x: [24, 30],y: [10, 18], side: "tl", rot: [-16, -6],  vis: "hidden xl:block" },
  { x: [24, 30],y: [8, 16],  side: "tr", rot: [8, 18],    vis: "hidden xl:block" },
];

function buildHeroCards() {
  // Shuffle card pool + sutra ids fresh on every page load
  const shuffledCards = [...CARD_POOL].sort(() => Math.random() - 0.5);
  const shuffledSutras = Array.from({ length: 52 }, (_, i) => i + 1).sort(() => Math.random() - 0.5);

  return SLOT_ZONES.map((zone, i) => {
    const x = `${rand(zone.x[0], zone.x[1]).toFixed(1)}%`;
    const y = `${rand(zone.y[0], zone.y[1]).toFixed(1)}%`;
    const pos = {};
    if (zone.side.startsWith("t") || zone.side.startsWith("m")) pos.top = y; else pos.bottom = y;
    if (zone.side.endsWith("l") || zone.side === "ml") pos.left = x; else pos.right = x;
    return {
      src: shuffledCards[i % shuffledCards.length],
      rot: rand(zone.rot[0], zone.rot[1]),
      vis: zone.vis,
      sutraId: shuffledSutras[i] || 1,
      ...pos,
    };
  });
}

export function AnimatedWords({ text, className, delay = 0 }) {
  return (
    <span className={className}>
      {text.split(" ").map((word, i) => (
        <motion.span key={i}
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: delay + i * 0.07, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block mr-[0.25em]">{word}</motion.span>
      ))}
    </span>
  );
}

function ScatteredCard({ card, index, onSelect }) {
  const { src, rot, vis, sutraId, ...pos } = card;
  return (
    <motion.div
      className={`absolute select-none cursor-pointer
        w-[112px] sm:w-[100px] md:w-[112px] lg:w-[124px] xl:w-[136px]
        aspect-[5/7] ${vis}`}
      style={{ ...pos, touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
      initial={{ opacity: 0, rotate: rot, scale: 0.7 }}
      animate={{ opacity: 1, rotate: rot, scale: 1 }}
      transition={{ delay: 0.5 + index * 0.08, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{
        scale: 1.2,
        rotate: 0,
        y: -10,
        zIndex: 50,
        transition: { type: "spring", stiffness: 280, damping: 18 },
      }}
      onTap={() => onSelect(card)}
      data-testid={`hero-card-${sutraId}`}
    >
      <div
        className="w-full h-full rounded-xl overflow-hidden"
        style={{ boxShadow: "0 14px 30px rgba(70,70,70,0.18)" }}
      >
        <img src={src} alt="" loading="lazy" decoding="async" width="160" height="224" className="w-full h-full object-cover" />
      </div>
    </motion.div>
  );
}

function CardLightbox({ card, onClose }) {
  const sutra = sutras.find((s) => s.id === card.sutraId);
  const [flipped, setFlipped] = useState(false);

  // Auto-flip to back after pop-in
  useEffect(() => {
    const t = setTimeout(() => setFlipped(true), 380);
    return () => clearTimeout(t);
  }, []);

  // Esc to close
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center px-5 py-10"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      onClick={onClose}
      data-testid="card-lightbox"
    >
      <div className="absolute inset-0 bg-[#464646]/55 backdrop-blur-xl" />

      {/* Close hint */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 sm:top-8 sm:right-8 z-10 w-10 h-10 rounded-full bg-white/80 backdrop-blur-md hover:bg-white shadow-lg flex items-center justify-center text-[#464646] transition-colors"
        data-testid="lightbox-close-btn"
        aria-label="Close"
      >
        <X size={18} />
      </button>

      <motion.div
        className="relative w-[260px] sm:w-[320px] md:w-[380px] lg:w-[420px] aspect-[5/7]"
        style={{ perspective: 1400 }}
        initial={{ scale: 0.35, opacity: 0, rotate: -8 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        exit={{ scale: 0.4, opacity: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 26 }}
        onClick={(e) => { e.stopPropagation(); setFlipped((f) => !f); }}
      >
        <motion.div
          className="relative w-full h-full cursor-pointer"
          style={{ transformStyle: "preserve-3d" }}
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Front */}
          <div
            className="absolute inset-0 rounded-2xl sm:rounded-3xl overflow-hidden"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              boxShadow: "0 40px 90px rgba(70,70,70,0.35), 0 0 60px rgba(150,174,180,0.25)",
            }}
          >
            <img src={card.src} alt="" decoding="async" width="420" height="588" className="w-full h-full object-cover" />
          </div>

          {/* Back */}
          <div
            className="absolute inset-0 rounded-2xl sm:rounded-3xl overflow-hidden flex flex-col items-center justify-center text-center p-6 sm:p-8"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              background: "linear-gradient(155deg, #fff8f3 0%, #f3ece4 55%, #e8e0d6 100%)",
              border: "1px solid rgba(150,174,180,0.4)",
              boxShadow: "0 40px 90px rgba(70,70,70,0.35), inset 0 0 60px rgba(166,193,168,0.18)",
            }}
          >
            <div className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#96aeb4] font-semibold mb-3 sm:mb-4">
              Sutra {sutra?.id}
            </div>
            <div className="w-8 h-px bg-[#96aeb4]/60 mb-3 sm:mb-4" />
            <h3 className="text-xl sm:text-2xl md:text-3xl leading-tight font-bold text-[#464646] font-serif mb-4 sm:mb-5">
              {sutra?.title}
            </h3>
            <p className="text-xs sm:text-sm md:text-base leading-relaxed text-[#464646]/70 max-w-[90%]">
              {sutra?.description}
            </p>
            <div className="mt-5 sm:mt-7 text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-[#464646]/35">
              Tap to flip · Tap outside to close
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export default function HeroSection() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const heroCards = useMemo(() => buildHeroCards(), []);
  const { c } = useCopy();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  // Lock body scroll while lightbox is open
  useEffect(() => {
    document.body.style.overflow = selectedCard ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [selectedCard]);

  const scrollTo = (id) => { document.querySelector(id)?.scrollIntoView({ behavior: "smooth" }); setMenuOpen(false); };

  return (
    <>
      {/* Nav */}
      <header data-testid="site-header"
        className={`fixed top-0 w-full z-[60] transition-all duration-500 ${scrolled ? "backdrop-blur-xl bg-[#fff8f3]/80 border-b border-[#464646]/10 shadow-sm shadow-[#464646]/5" : ""}`}>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between h-14 md:h-[68px]">
          <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
            <img src={LOGO_SVG} alt="Calmasutras" fetchpriority="high" decoding="async" width="160" height="40" className="h-6 md:h-8" style={{ filter: "invert(1)" }} data-testid="logo" />
          </a>
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((l) => (
              l.external ? (
                <a key={l.href} href={l.href}
                  className="text-[12px] text-[#464646]/60 hover:text-[#464646] transition-colors duration-300 tracking-widest uppercase"
                  data-testid={`nav-link-${l.label.toLowerCase()}`}>{l.label}</a>
              ) : (
                <button key={l.href} onClick={() => scrollTo(l.href)}
                  className="text-[12px] text-[#464646]/60 hover:text-[#464646] transition-colors duration-300 tracking-widest uppercase">{l.label}</button>
              )
            ))}
            <button onClick={() => scrollTo("#begin-journey")} data-testid="nav-buy-btn"
              className="text-[12px] bg-[#464646] text-white hover:bg-[#96aeb4] hover:text-white rounded-full px-5 py-2 font-semibold transition-all duration-300 shadow-sm whitespace-nowrap">
              {c("heroSecondaryCta")}</button>
          </nav>
          <button className="md:hidden text-[#464646]/70" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn" aria-label="Toggle menu">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-[#fff8f3]/95 backdrop-blur-xl border-t border-[#464646]/10 px-5 py-4" data-testid="mobile-nav">
            {navLinks.map((l) => (
              l.external ? (
                <a key={l.href} href={l.href} className="block w-full text-left py-2.5 text-[#464646]/60 text-base">{l.label}</a>
              ) : (
                <button key={l.href} onClick={() => scrollTo(l.href)} className="block w-full text-left py-2.5 text-[#464646]/60 text-base">{l.label}</button>
              )
            ))}
            <button onClick={() => scrollTo("#begin-journey")} className="w-full mt-3 bg-[#464646] text-white rounded-full py-2.5 font-semibold text-sm">{c("heroSecondaryCta")}</button>
          </div>
        )}
      </header>

      {/* Hero - scattered cards + centered content */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16" data-testid="hero-section">
        {/* Soft halo behind logo */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[40%] w-[680px] h-[680px] max-w-[90vw] max-h-[90vw] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(150,174,180,0.35) 0%, rgba(166,193,168,0.18) 35%, transparent 70%)" }} />
        {/* Scattered interactive cards */}
        {heroCards.map((card, i) => (
          <ScatteredCard key={i} card={card} index={i} onSelect={setSelectedCard} />
        ))}

        {/* Center content - narrow column on mobile so side cards have tappable margin */}
        <div className="relative z-20 flex flex-col items-center text-center px-4 py-16 sm:py-20 w-[78%] sm:w-auto sm:max-w-[640px] mx-auto">
          <motion.img src={LOGO_PNG} alt="Calmasutras"
            fetchpriority="high" decoding="async" width="360" height="200"
            className="w-60 sm:w-64 md:w-80 lg:w-[360px] mb-7 sm:mb-8"
            style={{ filter: "invert(1)" }}
            data-testid="hero-banner"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} />

          <motion.p className="text-base sm:text-base md:text-lg text-[#464646]/70 max-w-[260px] sm:max-w-md leading-relaxed font-medium"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }}>
            {c("heroSubline")}
          </motion.p>

          <motion.div className="flex items-center gap-3 sm:gap-5 mt-6 sm:mt-7 text-[11px] sm:text-[12px] uppercase tracking-[0.3em] text-[#464646]/40"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
            <span className="text-gradient-mist font-semibold">{c("heroPill1")}</span>
            <span className="w-1 h-1 rounded-full bg-[#464646]/25" />
            <span className="text-gradient-sage font-semibold">{c("heroPill2")}</span>
            <span className="w-1 h-1 rounded-full bg-[#464646]/25" />
            <span className="text-gradient-gold font-semibold">{c("heroPill3")}</span>
          </motion.div>

          <motion.div className="flex flex-col sm:flex-row gap-3 mt-8 sm:mt-9 w-full sm:w-auto"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}>
            <button onClick={() => scrollTo("#begin-journey")} data-testid="hero-buy-ebook-btn"
              className="bg-[#464646] text-white rounded-full px-7 py-3.5 text-sm sm:text-sm font-semibold hover:bg-[#96aeb4] transition-all duration-300 shadow-md shadow-[#464646]/10">
              {c("heroPrimaryCta")}</button>
            <button onClick={() => scrollTo("#begin-journey")} data-testid="hero-buy-cards-btn"
              className="border border-[#464646]/25 bg-white/50 backdrop-blur-sm text-[#464646]/85 hover:text-[#464646] hover:border-[#464646]/50 rounded-full px-7 py-3.5 text-sm font-semibold transition-all duration-300">
              {c("heroSecondaryCta")}</button>
          </motion.div>

        </div>

        {/* Scroll indicator */}
        <motion.div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-20"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}>
          <motion.div animate={{ y: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
            <ArrowDown size={12} className="text-[#464646]/30" />
          </motion.div>
        </motion.div>
      </section>

      {/* Marquee - brand mist→sage gradient */}
      <div className="py-4 overflow-hidden relative" data-testid="marquee"
        style={{ background: "linear-gradient(90deg, #96aeb4, #a6c1a8, #b3ccb8, #a6c1a8, #96aeb4)" }}>
        <div className="marquee-track">
          {[...Array(8)].map((_, i) => (
            <span key={i} className="text-sm sm:text-base font-serif font-bold tracking-wider text-white px-6 sm:px-8 whitespace-nowrap">
              {c("heroMarquee")}
            </span>
          ))}
        </div>
      </div>

      {/* Card pop-out lightbox */}
      <AnimatePresence>
        {selectedCard && (
          <CardLightbox card={selectedCard} onClose={() => setSelectedCard(null)} />
        )}
      </AnimatePresence>
    </>
  );
}
